import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getService } from '../../../api/services/id';
import { ContentsHeader } from '../../../components/common/ContentsHeader';
import { ServiceType } from '../../../types/resources/ServiceType';
import styles from './styles.module.scss';

export const ShowService = () => {
  const { service_id } = useParams();
  const [service, setService] = useState<ServiceType>();
  const { showToast } = useToast();
  const init = async () => {
    if (!service_id) return;
    try {
      const serviceData = await getService(service_id);
      setService(serviceData.service);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    }
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <div className={styles.root}>
      <ContentsHeader title='サービス詳細' />
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>基本情報</h2>

            <div className={styles.formWrapper}>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>カテゴリ</div>
                <div className={styles.formValue} data-testid='category'>
                  {service?.category?.name || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>提供会社</div>
                <div className={styles.formValue} data-testid='provider-company'>
                  {service?.company?.name || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>サービス名</div>
                <div className={styles.formValue} data-testid='service-name'>
                  {service?.title || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>ウェブサイトURL</div>
                <div className={styles.formValue} data-testid='website-url'>
                  {service?.site_url || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>サービスの概要</div>
                <div className={styles.formValue} data-testid='service-overview'>
                  {service?.overview || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>サービスの詳細</div>
                <div className={styles.formValue} data-testid='service-detail'>
                  {service?.detail || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>対応地区</div>
                <div className={styles.formValue} data-testid='service-area'>
                  {service?.supported_areas || '-'}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.gap}></div>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>利用情報</h2>

            <div className={styles.formWrapper}>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>クーポンコード</div>
                <div className={styles.formValue} data-testid='coupon-code'>
                  {service?.coupon_code || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>クーポン利用サイト</div>
                <div className={styles.formValue} data-testid='coupon-usage-site'>
                  {service?.coupon_url || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>クーポンの利用案内</div>
                <div className={styles.formValue} data-testid='coupon-usage-instruction'>
                  {service?.coupon_text || '-'}
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>注意事項</div>
                <div className={styles.formValue} data-testid='notes'>
                  {service?.important_information || '-'}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.gap}></div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>メニュー</h2>

            <div className={styles.formWrapper}>
              <div className={styles.menuGrid}>
                {service?.service_menus.map(menu => (
                  <div key={menu.id} className={styles.menuCard} data-testid={`menu-${menu.id}`}>
                    <h3 className={styles.menuTitle}>メニュー{menu.id}</h3>
                    <div className={styles.menuContent}>
                      <div className={styles.menuRow}>
                        <div className={styles.menuLabel}>メニュー名称</div>
                        <div className={styles.menuValue}>{menu.menu_name}</div>
                      </div>
                      <div className={styles.menuRow}>
                        <div className={styles.menuLabel}>割引価格</div>
                        <div className={styles.menuValue}>¥{menu.discounted_price}</div>
                      </div>
                      <div className={styles.menuRow}>
                        <div className={styles.menuLabel}>定価</div>
                        <div className={styles.menuValue}>¥{menu.regular_price}</div>
                      </div>
                      <div className={styles.menuRow}>
                        <div className={styles.menuLabel}>割引・期間</div>
                        <div className={styles.menuValue}>{menu.discount_detail}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.gap}></div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>画像</h2>
            <div className={styles.formWrapper}>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>メイン画像</div>
                <div className={styles.formValue} data-testid='main-image'>
                  {service?.main_image?.url ? (
                    <div className={styles.imageContainer}>
                      <img
                        src={service.main_image.url}
                        alt={service.title || 'Service main image'}
                        className={styles.mainImage}
                        onError={e => {
                          e.currentTarget.src = '/placeholder-image.png';
                          e.currentTarget.className = `${styles.mainImage} ${styles.errorImage}`;
                        }}
                      />
                    </div>
                  ) : (
                    <div className={styles.noImage}>No image available</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.gap}></div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>コンテンツ画像</h2>
            <div className={styles.formWrapper}>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>複数指定が可能です。</div>
                <div className={styles.formValue} data-testid='content-images'>
                  {service?.content_images?.length > 0 ? (
                    <div className={styles.imagesGrid}>
                      {service.content_images.map((image, index) => (
                        <div key={index} className={styles.imageContainer}>
                          <img
                            src={image.url}
                            alt={`${service.title || 'Service'} content image ${index + 1}`}
                            className={styles.contentImage}
                            onError={e => {
                              e.currentTarget.src = '/placeholder-image.png';
                              e.currentTarget.className = `${styles.contentImage} ${styles.errorImage}`;
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noImage}>No images available</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.gap}></div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>カセット画像</h2>
            <div className={styles.formWrapper}>
              <div className={styles.formRow}>
                <div className={styles.formLabel}>未指定の場合はメイン画像が利用されます。</div>
                <div className={styles.formValue} data-testid='cassette-image'>
                  {service?.cassette_image?.url ? (
                    <div className={styles.imageContainer}>
                      <img
                        src={service.main_image.url}
                        alt={service.title || 'Cassette image'}
                        className={styles.mainImage}
                        onError={e => {
                          e.currentTarget.src = '/placeholder-image.png';
                          e.currentTarget.className = `${styles.mainImage} ${styles.errorImage}`;
                        }}
                      />
                    </div>
                  ) : (
                    <div className={styles.noImage}>No image available</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
